/**
 * Fail-closed: LD init failure/timeout shows maintenance on.
 * Ad-blocker users are blocked from bypassing maintenance.
 * Use only during maintenance.
 */
import { useEffect, useState } from "react";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import {
  hasMaintenanceOverride,
  isMaintenanceModeFlagActive,
  MAINTENANCE_LD_INIT_TIMEOUT_MS,
  setMaintenanceModeFlagValue,
  setMaintenanceModeUnavailable,
} from "shared-utils";

import { FEATURE_FLAGS } from "../launchDarkly/utils";

export const useMaintenanceMode = () => {
  const flagValue = useFlags()?.[FEATURE_FLAGS.MaintenanceMode];
  const ldClient = useLDClient();
  const [isLoading, setIsLoading] = useState(true);
  const [isLdUnavailable, setIsLdUnavailable] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let timeoutId: ReturnType<typeof setTimeout>;

    const markUnavailable = () => {
      if (!isMounted) {
        return;
      }

      setIsLdUnavailable(true);
      setMaintenanceModeUnavailable(true);
      setIsLoading(false);
    };

    const markReady = () => {
      if (!isMounted) {
        return;
      }

      setIsLdUnavailable(false);
      setMaintenanceModeUnavailable(false);
      setIsLoading(false);
    };

    const clearUnavailableTimeout = () => {
      clearTimeout(timeoutId);
    };

    timeoutId = setTimeout(markUnavailable, MAINTENANCE_LD_INIT_TIMEOUT_MS);

    if (!ldClient) {
      return () => {
        isMounted = false;
        clearUnavailableTimeout();
      };
    }

    ldClient
      .waitForInitialization(MAINTENANCE_LD_INIT_TIMEOUT_MS / 1000)
      .then(markReady)
      .catch(markUnavailable)
      .finally(clearUnavailableTimeout);

    return () => {
      isMounted = false;
      clearUnavailableTimeout();
    };
  }, [ldClient]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setMaintenanceModeFlagValue(flagValue);
  }, [flagValue, isLoading]);

  return {
    isMaintenanceModeActive:
      !isLoading &&
      !hasMaintenanceOverride() &&
      (isMaintenanceModeFlagActive(flagValue) || isLdUnavailable),
    isLoading,
    isMaintenanceModeFlagActive: isMaintenanceModeFlagActive(flagValue),
  };
};
